import { useEffect, useState, useMemo } from "react";
import { API_URL } from "../config";
import { useFilteredImages } from "../hooks/useFilteredImages";
import { useImageCount } from "../hooks/useImageCount";
import { useSelection } from "../hooks/useSelection";
import FiltersLabelKeywords from "../components/filters/FiltersLabelKeywords";
import ImageTableView from "../components/ImageTableView";
import Notification from "../components/Notification";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function ImageTable() {
  const [limit, setLimit] = useState(100);
  const [deleted, setDeleted] = useState(false);

  const [availableLabels, setAvailableLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const [availableKeywords, setAvailableKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);

  const [keywordsMode, setKeywordsMode] = useState({ label: "or", value: "or" });

  const { selected, toggle, toggleAll, clear } = useSelection();
  const [refreshKey, setRefreshKey] = useState(0);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [after, setAfter] = useState(null);
  const [afterId, setAfterId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState(null);

  // Load labels
  useEffect(() => {
    const url = new URL(`${API_URL}/api/labels`);
    url.searchParams.append("deleted", deleted);
    if (keywordsMode?.value) url.searchParams.append("keywords_mode", keywordsMode.value);
    if (selectedKeywords.length > 0) {
      url.searchParams.append("keywords", selectedKeywords.map((k) => k.value).join(","));
    }

    fetch(url.toString())
      .then((res) => res.json())
      .then((data) => setAvailableLabels(data.map((l) => ({ value: l, label: l }))))
      .catch((err) => console.error("Error loading labels:", err));
  }, [selectedKeywords, keywordsMode, deleted]);

  // Load keywords
  useEffect(() => {
    const url = new URL(`${API_URL}/api/keywords`);
    url.searchParams.append("deleted", deleted);
    if (selectedLabels.length > 0) {
      url.searchParams.append("labels", selectedLabels.map((l) => l.value).join(","));
    }

    fetch(url.toString())
      .then((res) => res.json())
      .then((data) => setAvailableKeywords(data.map((k) => ({ value: k, label: k }))))
      .catch((err) => console.error("Error loading keywords:", err));
  }, [selectedLabels, deleted]);

  const filters = useMemo(() => ({
    labels: selectedLabels.map((l) => l.value),
    keywords: selectedKeywords.map((k) => k.value),
    keywords_mode: keywordsMode.value,
    sort_by: sortBy,
    sort_direction: sortDirection,
  }), [selectedLabels, selectedKeywords, keywordsMode, sortBy, sortDirection]);

  const { images, loading } = useFilteredImages({
    limit, deleted, filters, endpoint: "/api/images", after, afterId, refreshKey,
  });

  const { count: totalCount, loading: countLoading } = useImageCount({ filters, deleted });

  const handleAction = async () => {
    const endpoint = deleted ? "/api/restore" : "/api/delete";
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selected }),
      });
      if (!res.ok) throw new Error("Request failed");
      clear();
      setRefreshKey((prev) => prev + 1);
      setNotification({ message: deleted ? "Images restored" : "Images deleted", type: "success" });
    } catch (err) {
      setNotification({ message: `Error: ${err.message}`, type: "error" });
    }
  };

  const handleDownloadZip = async () => {
    const zip = new JSZip();
    const filtered = images.filter((img) => selected.includes(img.image_id));

    await Promise.all(
      filtered.map(async (img, idx) => {
        try {
          const response = await fetch(img.image_url);
          const blob = await response.blob();
          const name = (img.image_url.split("cdn.midjourney.com/")[1] || `img_${idx}.jpg`).replaceAll("/", "_");
          zip.file(name, blob);
        } catch (err) {
          console.error("Error downloading:", img.image_url);
        }
      })
    );

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "selected_images.zip");
    });
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortDirection("desc");
    }
    setAfter(null);
    setAfterId(null);
    setCurrentPage(1);
    clear();
  };

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <FiltersLabelKeywords
        availableLabels={availableLabels}
        selectedLabels={selectedLabels}
        setSelectedLabels={(v) => { setSelectedLabels(v); setAfter(null); setAfterId(null); setCurrentPage(1); clear(); }}
        availableKeywords={availableKeywords}
        selectedKeywords={selectedKeywords}
        setSelectedKeywords={(v) => { setSelectedKeywords(v); setAfter(null); setAfterId(null); setCurrentPage(1); clear(); }}
        keywordsMode={keywordsMode}
        setKeywordsMode={(v) => { setKeywordsMode(v); setAfter(null); setAfterId(null); setCurrentPage(1); clear(); }}
      />

      <select
        className="form-select mb-3"
        value={limit}
        onChange={(e) => { setLimit(parseInt(e.target.value)); setAfter(null); setAfterId(null); setCurrentPage(1); }}
      >
        <option value={100}>100</option>
        <option value={500}>500</option>
        <option value={1000}>1000</option>
      </select>

      <button
        className={`btn mb-3 ${deleted ? "btn-success" : "btn-danger"}`}
        onClick={handleAction}
        disabled={selected.length === 0}
      >
        {deleted ? "Restore Selected" : "Delete Selected"}
      </button>

      <button
        className="btn btn-secondary mb-3 ms-3"
        onClick={handleDownloadZip}
        disabled={selected.length === 0}
      >
        Download Selected
      </button>

      <div className="container">
        <div className="row align-items-end">
          <h2 className="col-3">Media Table</h2>
          <div className="col-7" />
          <button
            className="col text-end mb-0 btn btn-link"
            onClick={() => { setDeleted(!deleted); setAfter(null); setAfterId(null); setCurrentPage(1); clear(); }}
          >
            {deleted ? "Show Media" : "Show Hidden Media"}
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between mb-3 mt-3">
        <button
          className="btn btn-primary"
          disabled={loading || !after}
          onClick={() => { setAfter(null); setAfterId(null); clear(); setCurrentPage(1); }}
        >
          Back to Start
        </button>

        <div className="d-flex align-items-center">
          {countLoading ? (
            <span className="me-2">Loading...</span>
          ) : (
            <span className="me-2">
              Page {currentPage} of {Math.ceil(totalCount / limit)} | {totalCount}
            </span>
          )}
        </div>

        <button
          className="btn btn-primary"
          disabled={loading || images.length < limit}
          onClick={() => {
            const last = images[images.length - 1];
            if (last) {
              setAfter(last.created_at);
              setAfterId(last.image_id);
              clear();
              setCurrentPage((prev) => prev + 1);
            }
          }}
        >
          Next Page
        </button>
      </div>

      <ImageTableView
        images={images}
        selected={selected}
        onToggle={toggle}
        onToggleAll={toggleAll}
        idField="image_id"
        onSort={handleSort}
        sortBy={sortBy}
        sortDirection={sortDirection}
        extraColumns={[
          { header: "Keywords", render: (img) => (img.keywords ? img.keywords.join(", ") : "-") },
          { header: "View Count", render: (img) => (img.total_views ? img.total_views : "-") },
          {
            header: "Created At", key: "created_at", sortable: false,
            render: (img) => new Date(img.created_at).toLocaleDateString(),
          },
        ]}
      />
    </div>
  );
}

export default ImageTable;
